import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import {
  claimsFromSession,
  getCurrentSession,
  idToken,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  type Claims,
} from './cognito';

interface AuthState {
  loading: boolean;
  session: CognitoUserSession | null;
  claims: Claims | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  getToken: () => string | null;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCurrentSession()
      .then((s) => setSession(s))
      .finally(() => setLoading(false));
  }, []);

  const signIn: AuthState['signIn'] = useCallback(async (email, password) => {
    const result = await cognitoSignIn(email, password);
    setSession(result.session);
  }, []);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setSession(null);
  }, []);

  const getToken = useCallback(() => (session ? idToken(session) : null), [session]);
  const refresh = useCallback(async () => {
    const s = await getCurrentSession();
    setSession(s);
  }, []);

  const claims = useMemo(() => (session ? claimsFromSession(session) : null), [session]);

  const value = useMemo<AuthState>(
    () => ({ loading, session, claims, signIn, signOut, getToken, refresh }),
    [loading, session, claims, signIn, signOut, getToken, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
